import { useSyncExternalStore } from 'react'

/**
 * Generation counter for the lazily-loaded route chunks.
 *
 * `React.lazy` memoises the *rejection* of its loader: once the payload status
 * flips to rejected it re-throws that cached error on every later render and
 * never calls the loader again. So resetting an error boundary is not enough to
 * re-attempt a failed chunk fetch — the lazy component itself has to be
 * recreated.
 *
 * Bumping this generation is what makes that happen: `lazyPage` keys its cached
 * `React.lazy` component on the current value, so a bump hands the route a
 * brand-new lazy component whose loader runs from scratch.
 */

let generation = 0
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getLazyRouteGeneration(): number {
  return generation
}

/** Discard the memoised chunk loaders so the next render re-runs `import()`. */
export function retryLazyRoutes(): void {
  generation += 1
  for (const listener of listeners) listener()
}

export function useLazyRouteGeneration(): number {
  return useSyncExternalStore(subscribe, getLazyRouteGeneration, getLazyRouteGeneration)
}
